/**
 * What a month of AI spend says about itself, in a sentence — Story 23.3.
 *
 * Logic rather than a component, so `test/spend.test.ts` can hold it in plain node
 * the way the editor's store is held (`vitest.config.ts`).
 */

/** One UTC day of spend, as `GET /v1/orgs/{oid}/usage` returns it. */
export type SpendDay = {
  readonly day: number
  readonly calls: number
  readonly costMicros: number
  readonly inputTokens: number
  readonly cachedTokens: number
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

/** Monday first, the way a week is read. */
const inWeekOrder = (a: number, b: number) => ((a + 6) % 7) - ((b + 6) % 7)

/**
 * "Most of your spend is Tuesday and Wednesday." — the chart's caption in
 * `usage-cost.html`, said only when it is TRUE: when one weekday, or the two
 * biggest together, hold more than half of the month's spend. Otherwise null, and
 * the chart speaks for itself rather than the page claiming a pattern it lacks.
 * Days are UTC, the API's buckets (`services/usage/series.ts`).
 */
export function busiestWeekdays(days: readonly SpendDay[]): string | null {
  const byWeekday = new Map<number, number>()
  let spent = 0
  for (const day of days) {
    const weekday = new Date(day.day).getUTCDay()
    byWeekday.set(weekday, (byWeekday.get(weekday) ?? 0) + day.costMicros)
    spent += day.costMicros
  }
  if (spent <= 0) return null

  const [first, second] = [...byWeekday.entries()].sort((a, b) => b[1] - a[1])
  if (!first) return null
  if (first[1] * 2 > spent) return `Most of your spend is ${WEEKDAYS[first[0]]}.`
  if (second && (first[1] + second[1]) * 2 > spent) {
    const [earlier, later] = [first[0], second[0]].sort(inWeekOrder)
    return `Most of your spend is ${WEEKDAYS[earlier!]} and ${WEEKDAYS[later!]}.`
  }
  return null
}
